/**
 * 연차 관리 Firebase 서비스
 * Firestore를 사용한 연차 데이터 관리 서비스 레이어
 */

import { db } from '../firebase.js';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  Timestamp,
  DocumentReference,
  QueryConstraint,
  WriteBatch,
  serverTimestamp,
} from 'firebase/firestore';

import {
  AnnualLeaveBalance,
  LeaveRequest,
  LeaveSettings,
  LeaveSettlement,
  LeaveRequestInput,
  LeaveRequestFilters,
  LeaveStatus,
  ValidationResult,
  SettlementResult,
  MonthlyLeaveHistory,
} from '../types/annualLeave';

import { createLogger } from '../utils/logger.ts';
import { normalizeEmployeeId } from '../utils/employeeIdGenerator.ts';
import { Employee } from '../contexts/AppContext.tsx';

import {
  calculateSeniorityYears,
  calculateTotalLeaveDays,
  calculateValidPeriod,
  calculateDeductionDays,
  validateDateRange,
  parseDate,
  formatDate,
  formatYearMonth,
  isAnniversaryToday,
  calculateMonthlyLeaveDays,
  calculateSeniorityMonths,
} from '../utils/annual-leave/leaveCalculationUtils.ts';

// ===== Logger Setup =====
const logger = createLogger('AnnualLeaveService');
const DEBUG = false;

// ===== Collection References =====
const COLLECTIONS = {
  ANNUAL_LEAVE_BALANCES: 'annualLeaveBalances',
  LEAVE_REQUESTS: 'leaveRequests',
  LEAVE_SETTINGS: 'leaveSettings',
  LEAVE_SETTLEMENTS: 'leaveSettlements',
  MONTHLY_LEAVE_HISTORY: 'monthlyLeaveHistory',
  EMPLOYEES: 'employees',
  BUDGET: 'budget',
} as const;

// ===== Utility Functions =====
/**
 * Firebase에 저장하기 전에 undefined 값을 가진 필드를 제거합니다.
 * Firebase는 undefined 값을 저장할 수 없으므로 이 함수로 정리해야 합니다.
 */
const removeUndefinedFields = <T extends Record<string, any>>(obj: T): T => {
  const cleaned = {} as T;
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  }
  return cleaned;
};

// ===== 연차 잔액 관리 =====

/**
 * 직원의 현재 유효한 연차 잔액 조회
 * 호환성을 위해 문자열 ID도 처리하지만, 정규화된 숫자 ID 우선 조회
 */
export const getCurrentLeaveBalance = async (
  employeeId: number | string,
  currentDate: Date = new Date(),
  includeExpired: boolean = false
): Promise<AnnualLeaveBalance | null> => {
  try {
    DEBUG && logger.info(`[getCurrentLeaveBalance] Starting search - employeeId: ${employeeId} (Type: ${typeof employeeId}), includeExpired: ${includeExpired}`);
    DEBUG && logger.info(`[getCurrentLeaveBalance] Current date: ${currentDate.toISOString()}`);
    const balancesRef = collection(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES);

    // includeExpired가 true면 날짜 필터 없이 가장 최근 기록 조회
    if (includeExpired) {
      DEBUG && logger.info(`[getCurrentLeaveBalance] includeExpired=true - 만료된 기록 포함하여 조회`);

      // employeeId로 모든 기록 조회 후 가장 최근 것 선택
      let q = query(
        balancesRef,
        where('employeeId', '==', employeeId),
        orderBy('validFrom', 'desc'),
        limit(1)
      );

      let snapshot = await getDocs(q);
      DEBUG && logger.info(`[getCurrentLeaveBalance] 첫 번째 쿼리 결과: ${snapshot.size}개 문서 발견`);

      // 타입 변환 시도들
      if (snapshot.empty && typeof employeeId === 'string') {
        const numericId = parseInt(employeeId, 10);
        DEBUG && logger.info(`[getCurrentLeaveBalance] 문자열→숫자 변환 시도: "${employeeId}" → ${numericId}`);
        if (!isNaN(numericId)) {
          q = query(
            balancesRef,
            where('employeeId', '==', numericId),
            orderBy('validFrom', 'desc'),
            limit(1)
          );
          snapshot = await getDocs(q);
          DEBUG && logger.info(`[getCurrentLeaveBalance] 숫자 변환 쿼리 결과: ${snapshot.size}개 문서 발견`);
        }
      }

      if (snapshot.empty && typeof employeeId === 'number') {
        q = query(
          balancesRef,
          where('employeeId', '==', String(employeeId)),
          orderBy('validFrom', 'desc'),
          limit(1)
        );
        snapshot = await getDocs(q);
      }

      if (snapshot.empty && typeof employeeId === 'string' && isNaN(parseInt(employeeId, 10))) {
        q = query(
          balancesRef,
          where('employeeName', '==', employeeId),
          orderBy('validFrom', 'desc'),
          limit(1)
        );
        snapshot = await getDocs(q);
      }

      if (snapshot.empty) {
        DEBUG && logger.info(`[getCurrentLeaveBalance] 최종 결과: employeeId ${employeeId}에 대한 연차 데이터 없음`);
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();

      // 만료된 기록인지 체크하여 로그
      const isExpired = data.validUntil?.toDate() < currentDate;
      if (isExpired) {
        logger.warn(`[getCurrentLeaveBalance] ⚠️ 만료된 기록 반환 - validUntil: ${data.validUntil?.toDate()}`);
      }

      return {
        id: doc.id,
        ...data,
        employeeId: typeof data.employeeId === 'number'
          ? data.employeeId
          : parseInt(String(data.employeeId), 10) || data.employeeId,
        hireDate: data.hireDate?.toDate(),
        anniversaryDate: data.anniversaryDate?.toDate(),
        validFrom: data.validFrom?.toDate(),
        validUntil: data.validUntil?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as AnnualLeaveBalance;
    }

    // 기존 로직: 유효기간 내 기록만 조회
    // 1차 시도: employeeId 그대로 사용 (정규화된 데이터)
    let q = query(
      balancesRef,
      where('employeeId', '==', employeeId),
      where('validFrom', '<=', Timestamp.fromDate(currentDate)),
      where('validUntil', '>=', Timestamp.fromDate(currentDate)),
      limit(1)
    );

    let snapshot = await getDocs(q);
    DEBUG && logger.info(`[getCurrentLeaveBalance] 1차 시도 결과: ${snapshot.empty ? '없음' : '발견'}`);

    // 2차 시도: 타입 변환하여 조회 (마이그레이션 중 호환성)
    if (snapshot.empty && typeof employeeId === 'string') {
      // 문자열을 숫자로 변환 시도
      const numericId = parseInt(employeeId, 10);
      if (!isNaN(numericId)) {
        DEBUG && logger.info(`[getCurrentLeaveBalance] 2차 시도: 문자열 "${employeeId}"를 숫자 ${numericId}로 변환`);
        q = query(
          balancesRef,
          where('employeeId', '==', numericId),
          where('validFrom', '<=', Timestamp.fromDate(currentDate)),
          where('validUntil', '>=', Timestamp.fromDate(currentDate)),
          limit(1)
        );
        snapshot = await getDocs(q);
        DEBUG && logger.info(`[getCurrentLeaveBalance] 2차 시도 결과: ${snapshot.empty ? '없음' : '발견'}`);
      }
    }

    // 3차 시도: 숫자를 문자열로 변환 시도 (레거시 데이터)
    if (snapshot.empty && typeof employeeId === 'number') {
      DEBUG && logger.info(`[getCurrentLeaveBalance] 3차 시도: 숫자 ${employeeId}를 문자열 "${String(employeeId)}"로 변환`);
      q = query(
        balancesRef,
        where('employeeId', '==', String(employeeId)),
        where('validFrom', '<=', Timestamp.fromDate(currentDate)),
        where('validUntil', '>=', Timestamp.fromDate(currentDate)),
        limit(1)
      );
      snapshot = await getDocs(q);
      DEBUG && logger.info(`[getCurrentLeaveBalance] 3차 시도 결과: ${snapshot.empty ? '없음' : '발견'}`);

      // 레거시 데이터 발견 시 경고
      if (!snapshot.empty) {
        logger.warn(`[LEGACY DATA] 숫자 ID ${employeeId}에 대한 문자열 employeeId 발견 - 마이그레이션 필요`);
      }
    }

    // 4차 시도: 직원 이름으로 조회 (구형 데이터 호환)
    if (snapshot.empty && typeof employeeId === 'string' && isNaN(parseInt(employeeId, 10))) {
      DEBUG && logger.info(`[getCurrentLeaveBalance] 4차 시도: 직원 이름 "${employeeId}"로 직접 조회`);
      q = query(
        balancesRef,
        where('employeeName', '==', employeeId),
        where('validFrom', '<=', Timestamp.fromDate(currentDate)),
        where('validUntil', '>=', Timestamp.fromDate(currentDate)),
        limit(1)
      );
      snapshot = await getDocs(q);
      DEBUG && logger.info(`[getCurrentLeaveBalance] 4차 시도 결과: ${snapshot.empty ? '없음' : '발견'}`);

      if (!snapshot.empty) {
        logger.warn(`[LEGACY DATA] 직원 이름 "${employeeId}"로 데이터 발견 - employeeId 필드 정규화 필요`);
      }
    }

    if (snapshot.empty) {
      DEBUG && logger.info(`[getCurrentLeaveBalance] 최종 결과: employeeId ${employeeId}에 대한 연차 데이터 없음`);
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    DEBUG && logger.info(`[getCurrentLeaveBalance] 성공: 문서 ID="${doc.id}", employeeId=${data.employeeId}, employeeName=${data.employeeName}`);

    return {
      id: doc.id,
      ...data,
      employeeId: typeof data.employeeId === 'number'
        ? data.employeeId
        : parseInt(String(data.employeeId), 10) || data.employeeId,
      hireDate: data.hireDate?.toDate(),
      anniversaryDate: data.anniversaryDate?.toDate(),
      validFrom: data.validFrom?.toDate(),
      validUntil: data.validUntil?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
    } as AnnualLeaveBalance;
  } catch (error) {
    logger.error('[getCurrentLeaveBalance] 오류 발생:', error);
    throw error;
  }
};

/**
 * 입사 기념일 연차 생성
 * 입사 1년 이상 직원의 연차 생성 및 월차 소멸 처리
 */
export const createAnniversaryLeave = async (
  employee: any,
  anniversaryDate: Date,
  isLargeWorkplace: boolean
): Promise<AnnualLeaveBalance> => {
  try {
    const employeeId = employee.id;
    // 월차 이력은 append-only이므로 트랜잭션 밖에서 미리 조회한다.
    // (Firestore 트랜잭션 안의 비트랜잭션 getDocs는 격리 집합 밖 읽기 — 안티패턴)
    const monthlyHistoryRef = collection(db, COLLECTIONS.MONTHLY_LEAVE_HISTORY);
    const monthlySnapshot = await getDocs(
      query(monthlyHistoryRef, where('employeeId', '==', employeeId))
    );
    const expiredMonthlyDays = monthlySnapshot.empty ? 0 : monthlySnapshot.size;

    return await runTransaction(db, async (transaction) => {
      const hireDate = employee.hireDate;

      // 근속년수 계산
      const seniorityYears = calculateSeniorityYears(hireDate, anniversaryDate);

      // 1년 미만은 처리하지 않음
      if (seniorityYears < 1) {
        throw new Error('입사 1년 미만 직원은 연차 대상이 아닙니다.');
      }

      // 총 연차 일수 계산
      const settings = await getLeaveSettings();
      const totalDays = calculateTotalLeaveDays(seniorityYears, isLargeWorkplace, settings);

      // 유효기간 계산
      const { validFrom, validUntil } = calculateValidPeriod(anniversaryDate);

      // 문서 ID 생성 (employeeId_anniversaryDate)
      const docId = `${employeeId}_${formatDate(anniversaryDate)}`;
      const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId);

      // 기존 연차 확인 (중복 방지)
      const existingDoc = await transaction.get(balanceRef);
      if (existingDoc.exists()) {
        throw new Error('해당 연도의 연차가 이미 생성되었습니다.');
      }

      // 이전 월차 소멸 처리 (expiredMonthlyDays는 트랜잭션 밖에서 미리 조회함)

      // 사업장 규모 참조 월
      const referenceMonth = formatYearMonth(anniversaryDate);

      // 새 연차 잔액 생성
      const newBalance: AnnualLeaveBalance = {
        id: docId,
        employeeId,
        employeeName: employee.name,
        teamId: employee.teamId,
        hireDate,
        anniversaryDate,
        seniorityYears,
        validFrom,
        validUntil,
        totalDays,
        usedDays: 0,
        remainingDays: totalDays,
        pendingDays: 0,
        isLargeWorkplace,
        referenceMonth,
        monthlyLeaveDays: 0,
        expiredMonthlyDays,
        expiredAnnualDays: 0,
        locked: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Firestore에 저장
      transaction.set(balanceRef, {
        ...newBalance,
        hireDate: Timestamp.fromDate(hireDate),
        anniversaryDate: Timestamp.fromDate(anniversaryDate),
        validFrom: Timestamp.fromDate(validFrom),
        validUntil: Timestamp.fromDate(validUntil),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      DEBUG && logger.info(`연차 생성 완료: 직원 ${employee.name}, ${totalDays}일`);
      return newBalance;
    });
  } catch (error) {
    logger.error('Error creating anniversary leave:', error);
    throw error;
  }
};

/**
 * 월차 생성 (1년 미만 직원)
 */
export const createMonthlyLeave = async (
  employee: any,
  month: string // YYYY-MM
): Promise<AnnualLeaveBalance> => {
  try {
    const employeeId = employee.id;
    const hireDate = employee.hireDate;
    const currentDate = parseDate(`${month}-01`);

    // 근속 월수 계산
    const seniorityMonths = calculateSeniorityMonths(hireDate, currentDate);

    // 1년 이상이면 월차 대상 아님
    if (seniorityMonths >= 12) {
      throw new Error('입사 1년 이상 직원은 연차 대상입니다.');
    }

    // 월차 일수 계산 (누적)
    const monthlyDays = calculateMonthlyLeaveDays(seniorityMonths);

    // 문서 ID (1년 미만 직원용)
    const docId = `${employeeId}_monthly`;
    const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId);

    // 기존 월차 잔액 조회 또는 생성
    const existingDoc = await getDoc(balanceRef);

    if (existingDoc.exists()) {
      // 기존 월차 업데이트
      const existingData = existingDoc.data();
      const updatedBalance: Partial<AnnualLeaveBalance> = {
        monthlyLeaveDays: monthlyDays,
        remainingDays: monthlyDays - (existingData.usedDays || 0),
        updatedAt: new Date(),
      };

      await updateDoc(balanceRef, {
        ...updatedBalance,
        updatedAt: serverTimestamp(),
      });

      return {
        id: docId,
        ...existingData,
        ...updatedBalance,
      } as AnnualLeaveBalance;
    } else {
      // 새 월차 잔액 생성
      const newBalance: AnnualLeaveBalance = {
        id: docId,
        employeeId,
        employeeName: employee.name,
        teamId: employee.teamId,
        hireDate,
        anniversaryDate: new Date(), // 임시값
        seniorityYears: 0,
        validFrom: hireDate,
        validUntil: new Date(hireDate.getFullYear() + 1, hireDate.getMonth(), hireDate.getDate() - 1),
        totalDays: 0, // 월차는 연차가 아님
        usedDays: 0,
        remainingDays: monthlyDays,
        pendingDays: 0,
        isLargeWorkplace: false, // 월차는 사업장 규모 무관
        referenceMonth: month,
        monthlyLeaveDays: monthlyDays,
        expiredMonthlyDays: 0,
        expiredAnnualDays: 0,
        locked: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(balanceRef, {
        ...newBalance,
        hireDate: Timestamp.fromDate(hireDate),
        anniversaryDate: Timestamp.fromDate(newBalance.anniversaryDate),
        validFrom: Timestamp.fromDate(newBalance.validFrom),
        validUntil: Timestamp.fromDate(newBalance.validUntil),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 월차 이력 저장
      const historyRef = doc(collection(db, COLLECTIONS.MONTHLY_LEAVE_HISTORY));
      await setDoc(historyRef, {
        employeeId,
        month,
        earnedDays: 1,
        accumulatedDays: monthlyDays,
        createdAt: serverTimestamp(),
      });

      return newBalance;
    }
  } catch (error) {
    logger.error('Error creating monthly leave:', error);
    throw error;
  }
};

// ===== 연차 신청 관리 =====

/**
 * 연차 신청
 */
export const submitLeaveRequest = async (
  request: LeaveRequestInput,
  submittedBy: string
): Promise<LeaveRequest> => {
  try {
    // 현재 유효한 연차 잔액 조회
    const balance = await getCurrentLeaveBalance(request.employeeId);
    if (!balance) {
      throw new Error('유효한 연차 잔액이 없습니다.');
    }

    // 설정 조회
    const settings = await getLeaveSettings();

    // 날짜 파싱
    const startDate = parseDate(request.startDate);
    const endDate = parseDate(request.endDate);

    // 차감 일수 계산
    const deductionDays = calculateDeductionDays(
      request.leaveType,
      startDate,
      endDate,
      request.hours,
      request.halfDayType,
      settings
    );

    // 날짜 범위 검증
    const validation = validateDateRange(startDate, endDate, balance, deductionDays);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // 연차 종류별 설정 확인
    if (request.leaveType === 'half' && !settings?.halfDayLeaveEnabled) {
      throw new Error('반차가 비활성화되어 있습니다.');
    }
    if (request.leaveType === 'hourly' && !settings?.hourlyLeaveEnabled) {
      throw new Error('시간차가 비활성화되어 있습니다.');
    }

    // employeeName 필수 체크
    if (!request.employeeName) {
      throw new Error('직원 이름이 필요합니다.');
    }

    // 신청 문서 생성
    const requestRef = doc(collection(db, COLLECTIONS.LEAVE_REQUESTS));
    const newRequest: LeaveRequest = {
      id: requestRef.id,
      employeeId: request.employeeId,
      employeeName: request.employeeName,
      teamId: balance.teamId,
      leaveBalanceId: balance.id,
      leaveType: request.leaveType,
      halfDayType: request.halfDayType,
      hours: request.hours,
      startDate,
      endDate,
      deductionDays,
      reason: request.reason,
      status: 'pending',
      submittedAt: new Date(),
      submittedBy,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // undefined 필드 제거 후 Firebase에 저장
    const dataToSave = removeUndefinedFields({
      ...newRequest,
      startDate: Timestamp.fromDate(startDate),
      endDate: Timestamp.fromDate(endDate),
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // 신청 문서 저장 + pendingDays 증가를 하나의 트랜잭션으로 원자화한다.
    // (조회 시점의 balance.pendingDays로 덮어쓰면 동시 신청 시 lost update 발생 →
    //  트랜잭션 내에서 최신 pendingDays를 다시 읽어 증가. approve/reject 경로와 동일 패턴)
    const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, balance.id);
    await runTransaction(db, async (transaction) => {
      const balanceSnap = await transaction.get(balanceRef);
      const currentPending = balanceSnap.exists()
        ? (balanceSnap.data().pendingDays || 0)
        : balance.pendingDays;
      transaction.set(requestRef, dataToSave);
      transaction.update(balanceRef, {
        pendingDays: currentPending + deductionDays,
        updatedAt: serverTimestamp(),
      });
    });

    DEBUG && logger.info(`연차 신청 완료: ${request.employeeName}, ${deductionDays}일`);
    return newRequest;
  } catch (error) {
    logger.error('Error submitting leave request:', error);
    throw error;
  }
};

/**
 * 연차 승인
 */
export const approveLeaveRequest = async (
  requestId: string,
  approverId: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      // 신청 조회
      const requestRef = doc(db, COLLECTIONS.LEAVE_REQUESTS, requestId);
      const requestDoc = await transaction.get(requestRef);

      if (!requestDoc.exists()) {
        throw new Error('연차 신청을 찾을 수 없습니다.');
      }

      const request = requestDoc.data() as LeaveRequest;

      // 이미 처리된 신청 확인
      if (request.status !== 'pending') {
        throw new Error(`이미 ${request.status} 상태입니다.`);
      }

      // 연차 잔액 조회
      const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, request.leaveBalanceId);
      const balanceDoc = await transaction.get(balanceRef);

      if (!balanceDoc.exists()) {
        throw new Error('연차 잔액을 찾을 수 없습니다.');
      }

      const balance = balanceDoc.data() as AnnualLeaveBalance;

      // 잔액 재확인
      if (request.deductionDays > balance.remainingDays) {
        throw new Error('잔여 연차가 부족합니다.');
      }

      // 신청 승인 처리
      transaction.update(requestRef, {
        status: 'approved' as LeaveStatus,
        approvedBy: approverId,
        processedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 연차 잔액 업데이트
      transaction.update(balanceRef, {
        usedDays: balance.usedDays + request.deductionDays,
        remainingDays: balance.remainingDays - request.deductionDays,
        pendingDays: Math.max(0, balance.pendingDays - request.deductionDays),
        updatedAt: serverTimestamp(),
      });

      DEBUG && logger.info(`연차 승인 완료: ${request.employeeName}, ${request.deductionDays}일`);
    });
  } catch (error) {
    logger.error('Error approving leave request:', error);
    throw error;
  }
};

/**
 * 연차 반려
 */
export const rejectLeaveRequest = async (
  requestId: string,
  approverId: string,
  rejectionReason: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      // 신청 조회
      const requestRef = doc(db, COLLECTIONS.LEAVE_REQUESTS, requestId);
      const requestDoc = await transaction.get(requestRef);

      if (!requestDoc.exists()) {
        throw new Error('연차 신청을 찾을 수 없습니다.');
      }

      const request = requestDoc.data() as LeaveRequest;

      // 이미 처리된 신청 확인
      if (request.status !== 'pending') {
        throw new Error(`이미 ${request.status} 상태입니다.`);
      }

      // 연차 잔액 조회
      const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, request.leaveBalanceId);
      const balanceDoc = await transaction.get(balanceRef);

      if (balanceDoc.exists()) {
        const balance = balanceDoc.data() as AnnualLeaveBalance;

        // 예정된 연차 복구
        transaction.update(balanceRef, {
          pendingDays: Math.max(0, balance.pendingDays - request.deductionDays),
          updatedAt: serverTimestamp(),
        });
      }

      // 신청 반려 처리
      transaction.update(requestRef, {
        status: 'rejected' as LeaveStatus,
        approvedBy: approverId,
        rejectionReason,
        processedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      DEBUG && logger.info(`연차 반려 완료: ${request.employeeName}`);
    });
  } catch (error) {
    logger.error('Error rejecting leave request:', error);
    throw error;
  }
};

/**
 * 연차 신청 취소
 */
export const cancelLeaveRequest = async (
  requestId: string,
  cancellationReason: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      // 신청 조회
      const requestRef = doc(db, COLLECTIONS.LEAVE_REQUESTS, requestId);
      const requestDoc = await transaction.get(requestRef);

      if (!requestDoc.exists()) {
        throw new Error('연차 신청을 찾을 수 없습니다.');
      }

      const request = requestDoc.data() as LeaveRequest;

      // 취소 가능 상태 확인 (pending 또는 approved)
      if (request.status === 'rejected' || request.status === 'cancelled') {
        throw new Error('취소할 수 없는 상태입니다.');
      }

      // 연차 잔액 조회
      const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, request.leaveBalanceId);
      const balanceDoc = await transaction.get(balanceRef);

      if (balanceDoc.exists()) {
        const balance = balanceDoc.data() as AnnualLeaveBalance;

        if (request.status === 'approved') {
          // 승인된 연차 취소: 사용 일수 복구
          transaction.update(balanceRef, {
            usedDays: Math.max(0, balance.usedDays - request.deductionDays),
            remainingDays: balance.remainingDays + request.deductionDays,
            updatedAt: serverTimestamp(),
          });
        } else {
          // 대기 중인 연차 취소: 예정 일수 복구
          transaction.update(balanceRef, {
            pendingDays: Math.max(0, balance.pendingDays - request.deductionDays),
            updatedAt: serverTimestamp(),
          });
        }
      }

      // 신청 취소 처리
      transaction.update(requestRef, {
        status: 'cancelled' as LeaveStatus,
        cancellationReason,
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      DEBUG && logger.info(`연차 취소 완료: ${request.employeeName}`);
    });
  } catch (error) {
    logger.error('Error cancelling leave request:', error);
    throw error;
  }
};

/**
 * 연차 신청 목록 조회
 */
export const getLeaveRequests = async (
  filters?: LeaveRequestFilters
): Promise<LeaveRequest[]> => {
  try {
    const requestsRef = collection(db, COLLECTIONS.LEAVE_REQUESTS);
    const constraints: QueryConstraint[] = [];

    // 필터 적용
    if (filters?.employeeId) {
      constraints.push(where('employeeId', '==', filters.employeeId));
    }
    if (filters?.teamId) {
      constraints.push(where('teamId', '==', filters.teamId));
    }
    if (filters?.status && filters.status.length > 0) {
      constraints.push(where('status', 'in', filters.status));
    }
    if (filters?.startDate) {
      constraints.push(where('startDate', '>=', Timestamp.fromDate(filters.startDate)));
    }
    if (filters?.endDate) {
      constraints.push(where('endDate', '<=', Timestamp.fromDate(filters.endDate)));
    }
    if (filters?.leaveType) {
      constraints.push(where('leaveType', '==', filters.leaveType));
    }

    // 정렬 (최신순)
    constraints.push(orderBy('submittedAt', 'desc'));

    const q = query(requestsRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();

      // employeeId를 number로 정규화 (string으로 저장된 경우 처리)
      let normalizedEmployeeId = data.employeeId;
      if (typeof data.employeeId === 'string' && !isNaN(Number(data.employeeId))) {
        normalizedEmployeeId = Number(data.employeeId);
      } else if (typeof data.employeeId === 'number') {
        normalizedEmployeeId = data.employeeId;
      }

      // teamId가 undefined인 경우 빈 문자열로 설정
      const teamId = data.teamId || '';

      return {
        id: doc.id,
        ...data,
        employeeId: normalizedEmployeeId,
        teamId: teamId,
        employeeName: data.employeeName || '직원', // employeeName이 없는 경우 기본값
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
        submittedAt: data.submittedAt?.toDate(),
        processedAt: data.processedAt?.toDate(),
        cancelledAt: data.cancelledAt?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as LeaveRequest;
    });
  } catch (error) {
    logger.error('Error getting leave requests:', error);
    throw error;
  }
};

// ===== 연차 정산 =====

/**
 * 연차 정산 계산 (퇴사 시)
 */
export const calculateLeaveSettlement = async (
  employeeId: number,
  resignationDate: Date
): Promise<SettlementResult> => {
  try {
    // 연차 조회 (만료된 기록도 포함하여 가장 최근 것 사용)
    const balance = await getCurrentLeaveBalance(employeeId, resignationDate, true);

    if (!balance) {
      DEBUG && logger.info(`[calculateLeaveSettlement] 직원 ${employeeId}의 연차 데이터 없음`);
      // 연차가 없는 경우
      return {
        employeeId,
        employeeName: '',
        unusedDays: 0,
        averageDailyWage: 0,
        settlementAmount: 0,
        details: {
          totalDays: 0,
          usedDays: 0,
          remainingDays: 0,
        },
      };
    }

    // 만료된 기록 사용 중인 경우 경고
    if (balance.validUntil && balance.validUntil < resignationDate) {
      logger.warn(`[calculateLeaveSettlement] ⚠️ 만료된 연차 기록으로 정산 진행 - validUntil: ${balance.validUntil}`);
    }

    // 미사용 연차 일수
    const unusedDays = balance.remainingDays;

    // 평균임금 계산 (최근 3개월 급여 기준)
    const averageDailyWage = await calculateAverageDailyWage(employeeId, resignationDate);

    // 정산 금액 계산
    const settlementAmount = Math.round(unusedDays * averageDailyWage);

    return {
      employeeId,
      employeeName: balance.employeeName,
      unusedDays,
      averageDailyWage,
      settlementAmount,
      details: {
        totalDays: balance.totalDays,
        usedDays: balance.usedDays,
        remainingDays: balance.remainingDays,
        monthlyDays: balance.monthlyLeaveDays,
      },
    };
  } catch (error) {
    logger.error('Error calculating leave settlement:', error);
    throw error;
  }
};

/**
 * 평균 일일 임금 계산
 */
const calculateAverageDailyWage = async (
  employeeId: number,
  baseDate: Date
): Promise<number> => {
  try {
    // 최근 3개월 급여 데이터 조회
    const threeMonthsAgo = new Date(baseDate);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    // budget 컬렉션에서 급여 데이터 조회
    const budgetRef = collection(db, COLLECTIONS.BUDGET);
    const q = query(
      budgetRef,
      where('yearMonth', '>=', formatYearMonth(threeMonthsAgo)),
      where('yearMonth', '<=', formatYearMonth(baseDate)),
      orderBy('yearMonth', 'desc'),
      limit(3)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // 급여 데이터가 없는 경우 기본값
      return 100000; // 일 10만원 (임시값)
    }

    let totalWage = 0;
    let monthCount = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const employeeData = data.employees?.find((emp: any) => emp.id === employeeId);

      if (employeeData) {
        // 평균임금 계산 (인센티브 제외)
        const monthlyWage =
          (employeeData.salary || 0) +
          (employeeData.overtime || 0) +
          (employeeData.individual || 0) +
          (employeeData.meal || 0) +
          (employeeData.housing || 0);

        totalWage += monthlyWage;
        monthCount++;
      }
    });

    // 일일 평균임금 (월 평균 / 30일)
    if (monthCount > 0) {
      return Math.round(totalWage / monthCount / 30);
    }

    return 100000; // 기본값
  } catch (error) {
    logger.error('Error calculating average daily wage:', error);
    return 100000; // 에러 시 기본값
  }
};

/**
 * 연차 정산 처리
 */
export const processLeaveSettlement = async (
  settlement: SettlementResult,
  resignationDate: Date,
  processedBy: string
): Promise<LeaveSettlement> => {
  try {
    const settlementRef = doc(collection(db, COLLECTIONS.LEAVE_SETTLEMENTS));
    const newSettlement: LeaveSettlement = {
      id: settlementRef.id,
      employeeId: settlement.employeeId,
      employeeName: settlement.employeeName,
      resignationDate,
      settlementDate: new Date(),
      unusedLeaveDays: settlement.unusedDays,
      averageDailyWage: settlement.averageDailyWage,
      settlementAmount: settlement.settlementAmount,
      status: 'completed',
      notes: `연차: ${settlement.details.totalDays}일, 사용: ${settlement.details.usedDays}일`,
      processedAt: new Date(),
      processedBy,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(settlementRef, {
      ...newSettlement,
      resignationDate: Timestamp.fromDate(resignationDate),
      settlementDate: Timestamp.fromDate(new Date()),
      processedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // 연차 잔액 잠금 처리 (만료된 기록도 포함)
    const balance = await getCurrentLeaveBalance(settlement.employeeId, resignationDate, true);
    if (balance) {
      await updateDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, balance.id), {
        remainingDays: 0,
        expiredAnnualDays: balance.remainingDays,
        locked: true,
        updatedAt: serverTimestamp(),
      });
    }

    DEBUG && logger.info(`연차 정산 완료: ${settlement.employeeName}, ${settlement.settlementAmount}원`);
    return newSettlement;
  } catch (error) {
    logger.error('Error processing leave settlement:', error);
    throw error;
  }
};

// ===== 설정 관리 =====

/**
 * 연차 설정 조회
 */
export const getLeaveSettings = async (): Promise<LeaveSettings | null> => {
  try {
    const settingsRef = doc(db, COLLECTIONS.LEAVE_SETTINGS, 'config');
    const settingsDoc = await getDoc(settingsRef);

    if (!settingsDoc.exists()) {
      // 기본 설정 반환
      return getDefaultLeaveSettings();
    }

    const data = settingsDoc.data();
    return {
      ...data,
      updatedAt: data.updatedAt?.toDate(),
    } as LeaveSettings;
  } catch (error) {
    logger.error('Error getting leave settings:', error);
    return getDefaultLeaveSettings();
  }
};

/**
 * 기본 연차 설정
 */
export const getDefaultLeaveSettings = (): LeaveSettings => {
  return {
    baseLeaveDays: {
      largeWorkplace: 15,
      smallWorkplace: 12,
    },
    halfDayLeaveEnabled: true,
    halfDayHours: {
      morning: 3.5,
      afternoon: 4.5,
    },
    hourlyLeaveEnabled: false,
    minimumHourlyUnit: 1,
    workplaceSize: {
      isLargeWorkplace: true,
      autoDetect: false,
      thresholdEmployeeCount: 5,
    },
    seniorityBonusDays: {
      enabled: true,
      maxBonusDays: 10,
    },
    leavePromotion: {
      enabled: false,
      promotionMonthsBefore: 3,
    },
    updatedAt: new Date(),
    updatedBy: 'system',
  };
};

/**
 * 연차 설정 업데이트
 */
export const updateLeaveSettings = async (
  updates: Partial<LeaveSettings>,
  updatedBy: string
): Promise<void> => {
  try {
    const settingsRef = doc(db, COLLECTIONS.LEAVE_SETTINGS, 'config');

    await setDoc(settingsRef, {
      ...updates,
      updatedBy,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    DEBUG && logger.info('연차 설정 업데이트 완료');
  } catch (error) {
    logger.error('Error updating leave settings:', error);
    throw error;
  }
};

// ===== 유틸리티 함수 =====

/**
 * 전체 직원의 연차 잔액 조회
 */
export const getAllLeaveBalances = async (
  currentDate: Date = new Date(),
  includeExpired: boolean = false  // 기본값 false (비즈니스 로직용)
): Promise<AnnualLeaveBalance[]> => {
  try {
    DEBUG && logger.info(`[getAllLeaveBalances] includeExpired: ${includeExpired}`);
    const balancesRef = collection(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES);
    let q: Query;

    if (includeExpired) {
      // 만료된 기록도 포함하여 조회
      DEBUG && logger.info('[getAllLeaveBalances] 만료된 기록 포함하여 조회');
      q = query(balancesRef, orderBy('employeeName'));
    } else {
      // 유효기간 내 기록만 조회
      DEBUG && logger.info('[getAllLeaveBalances] 유효기간 내 기록만 조회');
      q = query(
        balancesRef,
        where('validFrom', '<=', Timestamp.fromDate(currentDate)),
        where('validUntil', '>=', Timestamp.fromDate(currentDate)),
        orderBy('employeeName')
      );
    }

    const snapshot = await getDocs(q);
    const balances = snapshot.docs.map(doc => {
      const data = doc.data();
      const balance = {
        id: doc.id,
        ...data,
        hireDate: data.hireDate?.toDate(),
        anniversaryDate: data.anniversaryDate?.toDate(),
        validFrom: data.validFrom?.toDate(),
        validUntil: data.validUntil?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as AnnualLeaveBalance;

      // 만료 여부 확인 및 로그
      if (includeExpired && balance.validUntil && balance.validUntil < currentDate) {
        DEBUG && logger.info(`[getAllLeaveBalances] ⚠️ 만료된 기록: ${balance.employeeName} - validUntil: ${balance.validUntil}`);
      }

      return balance;
    });

    DEBUG && logger.info(`[getAllLeaveBalances] 조회된 연차 잔액: ${balances.length}건`);
    return balances;
  } catch (error) {
    logger.error('Error getting all leave balances:', error);
    throw error;
  }
};

/**
 * 전체 직원의 연차 상태 조회 (연차 초기화 여부와 관계없이)
 * employees 컬렉션과 annualLeaveBalances를 조인하여
 * 모든 직원을 표시하고 연차가 없는 직원도 포함
 */
export const getAllEmployeeLeaveStatus = async (
  currentDate: Date = new Date(),
  includeExpired: boolean = true  // 기본값 true로 설정 (화면 표시용)
): Promise<AnnualLeaveBalance[]> => {
  try {
    DEBUG && logger.info(`=== getAllEmployeeLeaveStatus 디버깅 시작 ===`);
    DEBUG && logger.info(`[getAllEmployeeLeaveStatus] includeExpired: ${includeExpired}`);
    DEBUG && logger.info(`[getAllEmployeeLeaveStatus] currentDate: ${currentDate.toISOString()}`);

    // 1. 모든 직원 조회
    const employeesRef = collection(db, COLLECTIONS.EMPLOYEES);
    const employeesSnapshot = await getDocs(employeesRef);
    DEBUG && logger.info(`[getAllEmployeeLeaveStatus] 직원 수: ${employeesSnapshot.docs.length}`);

    // 직원 ID 타입 확인
    DEBUG && logger.info('[getAllEmployeeLeaveStatus] 직원 목록:');
    employeesSnapshot.docs.forEach(doc => {
      const employee = doc.data();
      const employeeId = employee.id || doc.id;
      DEBUG && logger.info(`  - ID: ${employeeId} (타입: ${typeof employeeId}), 이름: ${employee.name}`);
    });

    // 2. 연차 잔액 조회
    const balancesRef = collection(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES);
    let balancesSnapshot: QuerySnapshot;

    if (includeExpired) {
      // 만료된 기록도 포함하여 모든 기록 조회
      DEBUG && logger.info('[getAllEmployeeLeaveStatus] 만료된 기록 포함하여 조회');
      const allBalancesQuery = query(balancesRef);
      balancesSnapshot = await getDocs(allBalancesQuery);

      // 전체 문서 디버깅
      DEBUG && logger.info(`[getAllEmployeeLeaveStatus] annualLeaveBalances 컬렉션 전체 문서 수: ${balancesSnapshot.docs.length}`);
      if (balancesSnapshot.docs.length === 0) {
        logger.warn('[getAllEmployeeLeaveStatus] ⚠️ annualLeaveBalances 컬렉션이 비어있습니다!');
      } else {
        DEBUG && logger.info('[getAllEmployeeLeaveStatus] annualLeaveBalances 문서 목록:');
        balancesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          DEBUG && logger.info(`  - 문서 ID: ${doc.id}`);
          DEBUG && logger.info(`    employeeId: ${data.employeeId} (타입: ${typeof data.employeeId})`);
          DEBUG && logger.info(`    employeeName: ${data.employeeName}`);
          DEBUG && logger.info(`    validFrom: ${data.validFrom?.toDate ? data.validFrom.toDate() : data.validFrom}`);
          DEBUG && logger.info(`    validUntil: ${data.validUntil?.toDate ? data.validUntil.toDate() : data.validUntil}`);
          DEBUG && logger.info(`    totalDays: ${data.totalDays}, usedDays: ${data.usedDays}, remainingDays: ${data.remainingDays}`);
        });
      }

      // 각 직원별로 가장 최근 기록만 선택하기 위한 처리
      const latestBalanceMap = new Map<number, any>();
      DEBUG && logger.info('[getAllEmployeeLeaveStatus] 최신 기록 필터링 시작...');
      balancesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // employeeId 타입 정규화: 문자열이면 숫자로 변환 (중복 방지)
        const rawEmployeeId = data.employeeId;
        const employeeId = typeof rawEmployeeId === 'string'
          ? parseInt(rawEmployeeId, 10)
          : rawEmployeeId;
        DEBUG && logger.info(`  처리중: employeeId=${employeeId} (원본: ${rawEmployeeId}, 타입: ${typeof rawEmployeeId} → ${typeof employeeId})`);

        // 기존 기록이 없거나, 현재 기록이 더 최신인 경우 업데이트
        if (!latestBalanceMap.has(employeeId)) {
          DEBUG && logger.info(`    → 첫 번째 기록 추가`);
          latestBalanceMap.set(employeeId, { doc, data });
        } else if (data.validFrom?.toDate() > latestBalanceMap.get(employeeId).validFrom?.toDate()) {
          DEBUG && logger.info(`    → 더 최신 기록으로 업데이트`);
          latestBalanceMap.set(employeeId, { doc, data });
        } else {
          DEBUG && logger.info(`    → 기존 기록 유지`);
        }
      });
      DEBUG && logger.info(`[getAllEmployeeLeaveStatus] 필터링 완료: ${latestBalanceMap.size}명의 최신 기록`);

      // 가장 최근 기록만 사용하도록 snapshot 재구성
      const filteredDocs = Array.from(latestBalanceMap.values()).map(item => item.doc);
      balancesSnapshot = {
        ...balancesSnapshot,
        docs: filteredDocs
      } as QuerySnapshot;
    } else {
      // 기존 로직: 유효기간 내 기록만 조회
      DEBUG && logger.info('[getAllEmployeeLeaveStatus] 유효기간 내 기록만 조회');
      const balancesQuery = query(
        balancesRef,
        where('validFrom', '<=', Timestamp.fromDate(currentDate)),
        where('validUntil', '>=', Timestamp.fromDate(currentDate))
      );
      balancesSnapshot = await getDocs(balancesQuery);
    }

    // 3. 연차 데이터를 employeeId 기준으로 맵핑
    const balanceMap = new Map<number, AnnualLeaveBalance>();
    DEBUG && logger.info('[getAllEmployeeLeaveStatus] balanceMap 생성 시작...');
    balancesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      DEBUG && logger.info(`  문서 ${doc.id} 처리중...`);
      DEBUG && logger.info(`    raw employeeId: ${data.employeeId} (타입: ${typeof data.employeeId})`);

      // employeeId 타입 정규화: 문자열이면 숫자로 변환 (중복 방지)
      const normalizedEmployeeId = typeof data.employeeId === 'string'
        ? parseInt(data.employeeId, 10)
        : data.employeeId;

      const balance = {
        id: doc.id,
        ...data,
        employeeId: normalizedEmployeeId, // 정규화된 ID 사용
        hireDate: data.hireDate?.toDate(),
        anniversaryDate: data.anniversaryDate?.toDate(),
        validFrom: data.validFrom?.toDate(),
        validUntil: data.validUntil?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as AnnualLeaveBalance;

      DEBUG && logger.info(`    balance.employeeId: ${balance.employeeId} (정규화됨, 타입: ${typeof balance.employeeId})`);

      // 만료 여부 확인 및 로그
      if (includeExpired && balance.validUntil && balance.validUntil < currentDate) {
        DEBUG && logger.info(`    ⚠️ 만료된 기록: ${balance.employeeName} - validUntil: ${balance.validUntil}`);
      }

      balanceMap.set(normalizedEmployeeId, balance);
      DEBUG && logger.info(`    → balanceMap에 추가됨. 현재 Map 크기: ${balanceMap.size}`);
    });

    DEBUG && logger.info(`[getAllEmployeeLeaveStatus] balanceMap 생성 완료. 총 ${balanceMap.size}개 항목`);
    DEBUG && logger.info('[getAllEmployeeLeaveStatus] balanceMap 키 목록:', Array.from(balanceMap.keys()));

    // 4. 모든 직원에 대해 연차 상태 생성
    const allEmployeeLeaveStatus: AnnualLeaveBalance[] = [];
    DEBUG && logger.info('[getAllEmployeeLeaveStatus] 직원별 연차 상태 생성 시작...');

    // 직원별 연차 상태 처리
    // 참고: 모든 직원은 마이그레이션 후 숫자 ID (employee.id)를 가짐
    employeesSnapshot.docs.forEach(employeeDoc => {
      const employee = employeeDoc.data();
      const employeeId = employee.id || employeeDoc.id;

      DEBUG && logger.info(`  직원 처리: ID=${employeeId} (타입: ${typeof employeeId}), 이름=${employee.name}`);

      // 직원의 numeric ID 추출
      let numericId: number | undefined;

      // 1. employee.id가 숫자인 경우 사용
      if (typeof employee.id === 'number') {
        numericId = employee.id;
        DEBUG && logger.info(`    → employee.id 사용: ${numericId}`);
      }
      // 2. employee.id가 문자열 숫자인 경우 변환
      else if (typeof employee.id === 'string' && !isNaN(parseInt(employee.id, 10))) {
        numericId = parseInt(employee.id, 10);
        DEBUG && logger.info(`    → employee.id 문자열 변환: ${numericId}`);
      }
      // 3. ID가 없는 경우 경고 (마이그레이션 필요)
      else {
        logger.warn(`    ⚠️ ${employee.name}: numeric ID 없음 - ID 마이그레이션이 필요합니다`);
      }

      DEBUG && logger.info(`    최종 numericId: ${numericId}`);
      DEBUG && logger.info(`    balanceMap.has(${numericId}): ${numericId ? balanceMap.has(numericId) : false}`);

      // 연차 데이터 찾기 - numeric ID 우선, employeeName fallback
      if (numericId && balanceMap.has(numericId)) {
        DEBUG && logger.info(`    ✓ 연차 데이터 발견! ID: ${numericId}`);
        const balance = balanceMap.get(numericId)!;
        // balanceMap의 공유 객체를 직접 변이하지 않고 얕은 복사본을 push
        // (같은 balance가 이후 다른 직원에 재매칭될 때 employeeStatus 오염 방지)
        allEmployeeLeaveStatus.push({ ...balance, employeeStatus: employee.status || 'active' });
      } else {
        // employeeName으로 fallback 검색 (string ID 직원 대응)
        let foundByName: AnnualLeaveBalance | undefined;
        balanceMap.forEach((balance) => {
          if (balance.employeeName === employee.name) {
            foundByName = balance;
          }
        });

        if (foundByName) {
          DEBUG && logger.info(`    ✓ employeeName fallback 매칭: ${employee.name}`);
          // 공유 객체 변이 방지: 얕은 복사본을 push
          allEmployeeLeaveStatus.push({ ...foundByName, employeeStatus: employee.status || 'active' });
        } else {
          DEBUG && logger.info(`    ✗ 연차 데이터 없음 - 기본값 생성 (numericId: ${numericId})`);
          // 연차 데이터가 없는 경우 기본 정보로 생성
          let hireDate = null;
          if (employee.hireDate) {
            if (employee.hireDate.toDate) {
              hireDate = employee.hireDate.toDate();
            } else if (employee.hireDate instanceof Date) {
              hireDate = employee.hireDate;
            } else if (typeof employee.hireDate === 'string') {
              hireDate = new Date(employee.hireDate);
            }
          }

          // 근속 연수 계산 (입사일이 있는 경우)
          let seniorityYears = 0;
          let seniorityMonths = 0;
          if (hireDate && !isNaN(hireDate.getTime())) {
            seniorityYears = calculateSeniorityYears(hireDate, currentDate);
            seniorityMonths = calculateSeniorityMonths(hireDate, currentDate);
          }

          // 연차 미초기화 상태로 표시
          const emptyBalance: AnnualLeaveBalance = {
            id: `${numericId || employeeId}_placeholder`,
            employeeId: numericId || employeeId,
            employeeName: employee.name || '이름 없음',
            teamId: employee.team || employee.teamId || '',
            hireDate: hireDate,
            anniversaryDate: null,
            seniorityYears,
            validFrom: null,
            validUntil: null,
            totalDays: 0,
            usedDays: 0,
            remainingDays: 0,
            pendingDays: 0,
            isLargeWorkplace: false,
            referenceMonth: formatYearMonth(currentDate),
            monthlyLeaveDays: seniorityMonths < 12 ? seniorityMonths : 0,
            expiredMonthlyDays: 0,
            expiredAnnualDays: 0,
            locked: false,
            employeeStatus: employee.status || 'active',
            createdAt: null,
            updatedAt: null,
          };
          allEmployeeLeaveStatus.push(emptyBalance);
        }
      }
    });

    // 5. 직원 이름으로 정렬
    allEmployeeLeaveStatus.sort((a, b) => {
      const nameA = a.employeeName || '';
      const nameB = b.employeeName || '';
      return nameA.localeCompare(nameB, 'ko-KR');
    });

    DEBUG && logger.info(`전체 직원 연차 상태 조회: ${allEmployeeLeaveStatus.length}명`);
    return allEmployeeLeaveStatus;
  } catch (error) {
    logger.error('Error getting all employee leave status:', error);
    throw error;
  }
};

/**
 * 사업장 규모 확인
 * @param yearMonth - YYYY-MM 형식의 년월
 * @returns true: 5인 이상 사업장, false: 5인 미만 사업장
 */
export const checkWorkplaceSize = async (yearMonth: string): Promise<boolean> => {
  try {
    // budget/currentBudget 문서에서 monthlyData 내의 해당 월 데이터 확인
    const budgetRef = doc(db, COLLECTIONS.BUDGET, 'currentBudget');
    const budgetDoc = await getDoc(budgetRef);

    if (budgetDoc.exists()) {
      const data = budgetDoc.data();
      // monthlyData[yearMonth] 내의 applyOvertimeMultiplier 확인
      const monthlyData = data.monthlyData?.[yearMonth];

      // 5인 이상 사업장 여부 확인 (true: 5인 이상, false: 5인 미만)
      // applyOvertimeMultiplier 토글이 사업장 규모 판단에 사용됨
      const isLargeWorkplace = monthlyData?.applyOvertimeMultiplier === true;

      return isLargeWorkplace;
    }

    // 기본값: 5인 미만 사업장 (더 안전한 기본값)
    return false;
  } catch (error) {
    logger.error('[checkWorkplaceSize] 사업장 규모 확인 실패:', error);
    // 에러 시 5인 미만으로 처리 (더 보수적인 기본값)
    return false;
  }
};

/**
 * 특정 직원의 연차 신청 내역 조회
 */
export const getLeaveRequestsByEmployee = async (
  employeeId: number | string
): Promise<LeaveRequest[]> => {
  return getLeaveRequests({ employeeId });
};

/**
 * 전체 연차 신청 내역 조회
 */
export const getAllLeaveRequests = async (): Promise<LeaveRequest[]> => {
  return getLeaveRequests();
};

/**
 * 사업장 규모 변경에 따른 개별 직원 연차 재계산
 * 이미 생성된 연차의 totalDays를 재계산하면서
 * usedDays는 유지하고 remainingDays를 재계산
 *
 * @param employeeId - 직원 ID
 * @param forceRecalculate - 강제 재계산 여부
 * @returns 재계산된 연차 잔액 또는 null
 */
export const recalculateLeaveBalance = async (
  employeeId: number | string,
  forceRecalculate: boolean = false
): Promise<AnnualLeaveBalance | null> => {
  try {
    DEBUG && logger.info(`[recalculateLeaveBalance] 직원 ${employeeId} 연차 재계산 시작`);

    // 현재 연차 데이터 조회
    const balance = await getCurrentLeaveBalance(employeeId);
    if (!balance) {
      DEBUG && logger.info(`[recalculateLeaveBalance] 직원 ${employeeId}의 현재 연차 데이터가 없습니다.`);
      return null;
    }

    // 사업장 규모 재확인
    const currentWorkplaceSize = await checkWorkplaceSize(formatYearMonth(new Date()));

    // 재계산 필요 여부 판단
    if (!forceRecalculate && balance.isLargeWorkplace === currentWorkplaceSize) {
      DEBUG && logger.info(`[recalculateLeaveBalance] 사업장 규모 변경 없음 (현재: ${currentWorkplaceSize ? '5인 이상' : '5인 미만'})`);
      return balance; // 변경 없음
    }

    // 새로운 totalDays 계산
    const newTotalDays = calculateTotalLeaveDays(
      balance.seniorityYears,
      currentWorkplaceSize
    );

    DEBUG && logger.info(`[recalculateLeaveBalance] 총 연차 변경: ${balance.totalDays}일 → ${newTotalDays}일`);

    // remainingDays 재계산 (usedDays 유지)
    const newRemainingDays = newTotalDays - balance.usedDays - (balance.pendingDays || 0);

    // 문서 ID 찾기 (연차 데이터의 ID 형식에 따라)
    const balancesRef = collection(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES);
    const q = query(
      balancesRef,
      where('employeeId', '==', employeeId),
      where('validFrom', '<=', balance.validFrom),
      where('validUntil', '>=', balance.validUntil),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      logger.error(`[recalculateLeaveBalance] 문서를 찾을 수 없습니다.`);
      return null;
    }

    const docId = snapshot.docs[0].id;
    const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId);

    // 업데이트
    await updateDoc(balanceRef, {
      totalDays: newTotalDays,
      remainingDays: Math.max(0, newRemainingDays), // 음수 방지
      isLargeWorkplace: currentWorkplaceSize,
      referenceMonth: formatYearMonth(new Date()),
      updatedAt: serverTimestamp(),
    });

    DEBUG && logger.info(`[recalculateLeaveBalance] 직원 ${employeeId} 연차 재계산 완료`);

    return {
      ...balance,
      totalDays: newTotalDays,
      remainingDays: Math.max(0, newRemainingDays),
      isLargeWorkplace: currentWorkplaceSize,
    };
  } catch (error) {
    logger.error('[recalculateLeaveBalance] 연차 재계산 실패:', error);
    throw error;
  }
};

/**
 * 모든 직원의 연차를 사업장 규모 기준으로 일괄 재계산
 * 관리자 페이지에서 호출 가능
 *
 * @param forceRecalculate - 강제 재계산 여부
 * @returns 재계산 결과 요약
 */
export const recalculateAllLeaveBalances = async (
  forceRecalculate: boolean = true
): Promise<{
  success: number;
  failed: number;
  unchanged: number;
  details: Array<{
    employeeName: string;
    employeeId: number | string;
    oldTotalDays: number;
    newTotalDays: number;
    usedDays: number;
    status: 'updated' | 'unchanged' | 'failed';
    message?: string;
  }>;
}> => {
  const results = {
    success: 0,
    failed: 0,
    unchanged: 0,
    details: [] as any[],
  };

  try {
    DEBUG && logger.info('[recalculateAllLeaveBalances] 전체 직원 연차 재계산 시작');

    // 현재 사업장 규모 확인
    const currentWorkplaceSize = await checkWorkplaceSize(
      formatYearMonth(new Date())
    );

    DEBUG && logger.info(`[recalculateAllLeaveBalances] 현재 사업장 규모: ${currentWorkplaceSize ? '5인 이상' : '5인 미만'}`);

    // 모든 현재 유효한 연차 데이터 조회
    const balancesRef = collection(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES);
    const currentDate = Timestamp.fromDate(new Date());

    // 현재 유효한 연차 데이터만 조회
    const q = query(
      balancesRef,
      where('validFrom', '<=', currentDate),
      where('validUntil', '>=', currentDate)
    );

    const snapshot = await getDocs(q);

    DEBUG && logger.info(`[recalculateAllLeaveBalances] ${snapshot.size}개의 연차 데이터 발견`);

    for (const doc of snapshot.docs) {
      const balance = { id: doc.id, ...doc.data() } as AnnualLeaveBalance & { id: string };

      try {
        // 재계산 필요 여부 확인
        if (
          forceRecalculate ||
          balance.isLargeWorkplace !== currentWorkplaceSize
        ) {
          const newTotalDays = calculateTotalLeaveDays(
            balance.seniorityYears,
            currentWorkplaceSize
          );

          if (newTotalDays !== balance.totalDays) {
            const newRemainingDays = newTotalDays - balance.usedDays - (balance.pendingDays || 0);

            // 업데이트
            const balanceRef = doc.ref;
            await updateDoc(balanceRef, {
              totalDays: newTotalDays,
              remainingDays: Math.max(0, newRemainingDays),
              isLargeWorkplace: currentWorkplaceSize,
              updatedAt: serverTimestamp(),
            });

            results.details.push({
              employeeName: balance.employeeName,
              employeeId: balance.employeeId,
              oldTotalDays: balance.totalDays,
              newTotalDays,
              usedDays: balance.usedDays,
              status: 'updated',
              message: `${balance.totalDays}일 → ${newTotalDays}일 (사용: ${balance.usedDays}일)`,
            });

            results.success++;
          } else {
            results.details.push({
              employeeName: balance.employeeName,
              employeeId: balance.employeeId,
              oldTotalDays: balance.totalDays,
              newTotalDays,
              usedDays: balance.usedDays,
              status: 'unchanged',
              message: '변경사항 없음',
            });
            results.unchanged++;
          }
        } else {
          results.details.push({
            employeeName: balance.employeeName,
            employeeId: balance.employeeId,
            oldTotalDays: balance.totalDays,
            newTotalDays: balance.totalDays,
            usedDays: balance.usedDays,
            status: 'unchanged',
            message: '이미 현재 사업장 규모 반영됨',
          });
          results.unchanged++;
        }
      } catch (error) {
        logger.error(`[recalculateAllLeaveBalances] ${balance.employeeName} 처리 실패:`, error);
        results.failed++;
        results.details.push({
          employeeName: balance.employeeName,
          employeeId: balance.employeeId,
          oldTotalDays: balance.totalDays,
          newTotalDays: 0,
          usedDays: balance.usedDays,
          status: 'failed',
          message: error instanceof Error ? error.message : '알 수 없는 오류',
        });
      }
    }

    DEBUG && logger.info(`[recalculateAllLeaveBalances] 재계산 완료: 성공 ${results.success}, 변경없음 ${results.unchanged}, 실패 ${results.failed}`);

    return results;
  } catch (error) {
    logger.error('[recalculateAllLeaveBalances] 전체 재계산 실패:', error);
    throw error;
  }
};

// ===== 연차 신청 ID 마이그레이션 =====

/**
 * leaveRequests 컬렉션의 employeeId 및 leaveBalanceId를 마이그레이션합니다.
 * 직원 ID 마이그레이션 후 leaveRequests에 남아있는 이전 ID를 현재 ID로 업데이트합니다.
 *
 * 마이그레이션 대상:
 * - employeeId: 이전 ID → 현재 ID (employees 컬렉션 기준)
 * - leaveBalanceId: '이전ID_날짜' → '현재ID_날짜'
 *
 * @returns 마이그레이션 결과
 */
export const migrateLeaveRequestIds = async (): Promise<{
  migrated: number;
  skipped: number;
  errors: string[];
  details: Array<{
    requestId: string;
    employeeName: string;
    oldEmployeeId: number;
    newEmployeeId: number;
    oldBalanceId: string;
    newBalanceId: string;
  }>;
}> => {
  const result = {
    migrated: 0,
    skipped: 0,
    errors: [] as string[],
    details: [] as any[],
  };

  try {
    DEBUG && logger.info('[leaveRequest ID 마이그레이션] 시작');

    // 1. employees 컬렉션에서 현재 name → id 매핑 조회
    const employeesRef = collection(db, COLLECTIONS.EMPLOYEES);
    const employeesSnapshot = await getDocs(employeesRef);

    if (employeesSnapshot.empty) {
      logger.warn('[leaveRequest ID 마이그레이션] 직원 데이터 없음');
      return result;
    }

    const nameToIdMap: { [name: string]: number } = {};
    employeesSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const employeeName = docSnapshot.id;
      if (data.id && typeof data.id === 'number') {
        nameToIdMap[employeeName] = data.id;
      }
    });

    DEBUG && logger.info(`[leaveRequest ID 마이그레이션] 직원 ID 매핑: ${JSON.stringify(nameToIdMap)}`);

    // 2. 모든 leaveRequests 조회
    const requestsRef = collection(db, COLLECTIONS.LEAVE_REQUESTS);
    const requestsSnapshot = await getDocs(requestsRef);

    if (requestsSnapshot.empty) {
      DEBUG && logger.info('[leaveRequest ID 마이그레이션] 연차 신청 데이터 없음');
      return result;
    }

    DEBUG && logger.info(`[leaveRequest ID 마이그레이션] 총 ${requestsSnapshot.size}개 연차 신청 문서 발견`);

    // 3. 각 leaveRequest 확인 및 업데이트
    for (const docSnapshot of requestsSnapshot.docs) {
      const requestData = docSnapshot.data();
      const requestId = docSnapshot.id;
      const employeeName = requestData.employeeName;

      // 매핑에서 현재 ID 조회
      const currentId = nameToIdMap[employeeName];
      if (!currentId) {
        logger.warn(`[leaveRequest ID 마이그레이션] ${employeeName}: 매핑에서 ID를 찾을 수 없음`);
        result.errors.push(`${employeeName}: 직원 ID를 찾을 수 없음`);
        continue;
      }

      const oldEmployeeId = requestData.employeeId;
      const oldBalanceId = requestData.leaveBalanceId;

      // ID가 이미 올바른 경우 스킵
      if (oldEmployeeId === currentId) {
        logger.debug(`[leaveRequest ID 마이그레이션] ${employeeName}: ID 일치 (${currentId}), 스킵`);
        result.skipped++;
        continue;
      }

      // leaveBalanceId 재구성: '이전ID_날짜' → '현재ID_날짜'
      // 형식: '3_2025-06-01' → '5_2025-06-01'
      let newBalanceId = oldBalanceId;
      if (oldBalanceId && oldBalanceId.includes('_')) {
        const parts = oldBalanceId.split('_');
        if (parts.length >= 2) {
          // 첫 번째 부분(이전 ID)을 새 ID로 교체
          parts[0] = String(currentId);
          newBalanceId = parts.join('_');
        }
      }

      // 업데이트 실행
      try {
        const requestRef = doc(db, COLLECTIONS.LEAVE_REQUESTS, requestId);
        await updateDoc(requestRef, {
          employeeId: currentId,
          leaveBalanceId: newBalanceId,
          updatedAt: serverTimestamp(),
        });

        result.migrated++;
        result.details.push({
          requestId,
          employeeName,
          oldEmployeeId,
          newEmployeeId: currentId,
          oldBalanceId,
          newBalanceId,
        });

        DEBUG && logger.info(
          `[leaveRequest ID 마이그레이션] ${employeeName}: ` +
          `employeeId ${oldEmployeeId} → ${currentId}, ` +
          `leaveBalanceId '${oldBalanceId}' → '${newBalanceId}'`
        );
      } catch (error) {
        const errorMsg = `${employeeName}: 업데이트 실패 - ${error instanceof Error ? error.message : '알 수 없는 오류'}`;
        logger.error(`[leaveRequest ID 마이그레이션] ${errorMsg}`);
        result.errors.push(errorMsg);
      }
    }

    DEBUG && logger.info(
      `[leaveRequest ID 마이그레이션] 완료: ` +
      `${result.migrated}개 업데이트, ${result.skipped}개 스킵, ${result.errors.length}개 오류`
    );

    return result;
  } catch (error) {
    logger.error('[leaveRequest ID 마이그레이션] 전체 실패:', error);
    throw error;
  }
};

// ===== 연차 잔액 사용일수 동기화 =====

/**
 * annualLeaveBalances의 usedDays/pendingDays를 leaveRequests 기준으로 동기화합니다.
 * leaveRequest ID 마이그레이션 후 실행하여 잘못된 usedDays를 수정합니다.
 *
 * 동기화 로직:
 * - 각 annualLeaveBalance 문서에 대해 해당 leaveBalanceId를 참조하는 leaveRequests 집계
 * - approved 상태의 deductionDays 합산 → usedDays
 * - pending 상태의 deductionDays 합산 → pendingDays
 * - remainingDays = totalDays + monthlyLeaveDays - usedDays - pendingDays
 *
 * @returns 동기화 결과
 */
export const syncLeaveBalanceUsedDays = async (): Promise<{
  synced: number;
  unchanged: number;
  errors: string[];
  details: Array<{
    balanceId: string;
    employeeName: string;
    oldUsedDays: number;
    newUsedDays: number;
    oldPendingDays: number;
    newPendingDays: number;
    newRemainingDays: number;
  }>;
}> => {
  const result = {
    synced: 0,
    unchanged: 0,
    errors: [] as string[],
    details: [] as any[],
  };

  try {
    DEBUG && logger.info('[연차 사용일수 동기화] 시작');

    // 1. 모든 annualLeaveBalances 조회
    const balancesRef = collection(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES);
    const balancesSnapshot = await getDocs(balancesRef);

    if (balancesSnapshot.empty) {
      DEBUG && logger.info('[연차 사용일수 동기화] 연차 잔액 데이터 없음');
      return result;
    }

    DEBUG && logger.info(`[연차 사용일수 동기화] 총 ${balancesSnapshot.size}개 연차 잔액 문서 발견`);

    // 2. 모든 leaveRequests 조회하여 leaveBalanceId별로 그룹화
    const requestsRef = collection(db, COLLECTIONS.LEAVE_REQUESTS);
    const requestsSnapshot = await getDocs(requestsRef);

    const requestsByBalanceId: { [balanceId: string]: { usedDays: number; pendingDays: number } } = {};

    for (const docSnapshot of requestsSnapshot.docs) {
      const requestData = docSnapshot.data();
      const balanceId = requestData.leaveBalanceId;
      const status = requestData.status;
      const deductionDays = requestData.deductionDays || 0;

      if (!balanceId) continue;

      if (!requestsByBalanceId[balanceId]) {
        requestsByBalanceId[balanceId] = { usedDays: 0, pendingDays: 0 };
      }

      if (status === 'approved') {
        requestsByBalanceId[balanceId].usedDays += deductionDays;
      } else if (status === 'pending') {
        requestsByBalanceId[balanceId].pendingDays += deductionDays;
      }
    }

    DEBUG && logger.info(`[연차 사용일수 동기화] leaveRequests 집계 완료: ${Object.keys(requestsByBalanceId).length}개 잔액ID`);

    // 3. 각 annualLeaveBalance 업데이트
    for (const docSnapshot of balancesSnapshot.docs) {
      const balanceData = docSnapshot.data();
      const balanceId = docSnapshot.id;
      const employeeName = balanceData.employeeName || 'Unknown';

      try {
        const oldUsedDays = balanceData.usedDays || 0;
        const oldPendingDays = balanceData.pendingDays || 0;

        // leaveRequests에서 집계한 값 가져오기
        const calculated = requestsByBalanceId[balanceId] || { usedDays: 0, pendingDays: 0 };
        const newUsedDays = calculated.usedDays;
        const newPendingDays = calculated.pendingDays;

        // totalDays 계산 (연차 + 월차)
        const totalDays = (balanceData.totalDays || 0) + (balanceData.monthlyLeaveDays || 0);
        const newRemainingDays = Math.max(0, totalDays - newUsedDays - newPendingDays);

        // 변경사항 확인
        if (oldUsedDays === newUsedDays && oldPendingDays === newPendingDays) {
          logger.debug(`[연차 사용일수 동기화] ${employeeName}: 변경 없음 (사용: ${oldUsedDays}, 대기: ${oldPendingDays})`);
          result.unchanged++;
          continue;
        }

        // 업데이트 실행
        const balanceRef = doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, balanceId);
        await updateDoc(balanceRef, {
          usedDays: newUsedDays,
          pendingDays: newPendingDays,
          remainingDays: newRemainingDays,
          updatedAt: serverTimestamp(),
        });

        result.synced++;
        result.details.push({
          balanceId,
          employeeName,
          oldUsedDays,
          newUsedDays,
          oldPendingDays,
          newPendingDays,
          newRemainingDays,
        });

        DEBUG && logger.info(
          `[연차 사용일수 동기화] ${employeeName}: ` +
          `사용 ${oldUsedDays} → ${newUsedDays}, ` +
          `대기 ${oldPendingDays} → ${newPendingDays}, ` +
          `잔여 ${newRemainingDays}`
        );
      } catch (error) {
        const errorMsg = `${employeeName}: 업데이트 실패 - ${error instanceof Error ? error.message : '알 수 없는 오류'}`;
        logger.error(`[연차 사용일수 동기화] ${errorMsg}`);
        result.errors.push(errorMsg);
      }
    }

    DEBUG && logger.info(
      `[연차 사용일수 동기화] 완료: ` +
      `${result.synced}개 동기화, ${result.unchanged}개 변경없음, ${result.errors.length}개 오류`
    );

    return result;
  } catch (error) {
    logger.error('[연차 사용일수 동기화] 전체 실패:', error);
    throw error;
  }
};

// ===== 연차 잔액 일괄 초기화 =====

export interface InitializeResult {
  success: number;
  failed: number;
  skipped: number;
  updated: number;
  details: string[];
}

/**
 * 기존 leaveRequests에서 사용/대기 일수 계산
 * employeeId와 employeeName 모두 확인하여 ID 마이그레이션 불일치 방지
 */
const calculateExistingLeaveUsage = async (
  employeeId: number,
  employeeName: string
): Promise<{ usedDays: number; pendingDays: number }> => {
  try {
    const requests = await getLeaveRequests({ employeeId });

    // employeeName으로 필터링 (ID 마이그레이션 불일치 방지)
    const filteredRequests = requests.filter(req => req.employeeName === employeeName);

    if (requests.length !== filteredRequests.length) {
      logger.warn(
        `[initializeAllLeaveBalances] ${employeeName}: ID ${employeeId}로 ${requests.length}건 조회, ` +
        `이름 일치 ${filteredRequests.length}건 (ID 불일치 ${requests.length - filteredRequests.length}건 제외)`
      );
    }

    let usedDays = 0;
    let pendingDays = 0;

    for (const req of filteredRequests) {
      if (req.status === 'approved') {
        usedDays += req.deductionDays;
      } else if (req.status === 'pending') {
        pendingDays += req.deductionDays;
      }
    }

    return { usedDays, pendingDays };
  } catch (error) {
    logger.error(`[initializeAllLeaveBalances] ${employeeName}: 기존 연차 사용 내역 조회 실패:`, error instanceof Error ? error : undefined);
    return { usedDays: 0, pendingDays: 0 };
  }
};

/**
 * 모든 직원의 연차/월차 잔액을 일괄 초기화/재계산
 * - 신규 직원: 연차/월차 잔액 자동 생성
 * - 기존 직원 (forceRecalculate=true): 사업장 규모 변경 반영, usedDays/pendingDays 보존
 * - 변경 없는 직원: 스킵
 */
export const initializeAllLeaveBalances = async (
  employees: Employee[],
  forceRecalculate: boolean = true
): Promise<InitializeResult> => {
  let successCount = 0;
  let failedCount = 0;
  let skippedCount = 0;
  let updatedCount = 0;
  const details: string[] = [];

  try {
    const totalEmployees = employees.length;

    if (totalEmployees === 0) {
      throw new Error('직원 데이터가 없습니다.');
    }

    // 사업장 규모 확인 (기존 checkWorkplaceSize 함수 재사용)
    const currentMonth = formatYearMonth(new Date());
    const isLargeWorkplace = await checkWorkplaceSize(currentMonth);

    details.push(`사업장 규모: ${isLargeWorkplace ? '5인 이상' : '5인 미만'}`);

    // 각 직원 처리
    for (const employee of employees) {
      try {
        // ID 정규화
        let employeeId: number;
        try {
          employeeId = normalizeEmployeeId(employee.id);
        } catch (error) {
          details.push(`⚠️ ${employee.name}: ID 정규화 실패 (${employee.id}) - 건너뜀`);
          skippedCount++;
          continue;
        }

        // 입사일 확인
        if (!employee.hireDate) {
          details.push(`⚠️ ${employee.name}: 입사일 없음 - 건너뜀`);
          skippedCount++;
          continue;
        }

        // Date 객체로 변환
        let hireDate: Date;
        if (employee.hireDate instanceof Timestamp) {
          hireDate = (employee.hireDate as any).toDate();
        } else if (typeof employee.hireDate === 'string') {
          hireDate = new Date(employee.hireDate);
        } else {
          hireDate = employee.hireDate as Date;
        }

        // 유효한 날짜인지 확인
        if (isNaN(hireDate.getTime())) {
          details.push(`⚠️ ${employee.name}: 잘못된 입사일 형식 - 건너뜀`);
          skippedCount++;
          continue;
        }

        const now = new Date();
        const seniorityYears = calculateSeniorityYears(hireDate, now);
        const seniorityMonths = calculateSeniorityMonths(hireDate, now);

        // 입사 1개월 미만은 건너뜀
        if (seniorityMonths === 0) {
          details.push(`⚠️ ${employee.name}: 입사 1개월 미만 - 건너뜀`);
          skippedCount++;
          continue;
        }

        // 1년 이상 근무자: 연차 처리
        if (seniorityYears >= 1) {
          const currentAnniversaryDate = new Date(hireDate);
          currentAnniversaryDate.setFullYear(now.getFullYear());

          if (currentAnniversaryDate > now) {
            currentAnniversaryDate.setFullYear(currentAnniversaryDate.getFullYear() - 1);
          }

          const { validFrom, validUntil } = calculateValidPeriod(currentAnniversaryDate);
          const docId = `${employeeId}_${formatDate(currentAnniversaryDate)}`;

          const existingDoc = await getDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId));

          const currentSeniorityYears = Math.floor(
            (currentAnniversaryDate.getTime() - hireDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
          ) + 1;

          const totalDays = calculateTotalLeaveDays(currentSeniorityYears, isLargeWorkplace);

          if (!existingDoc.exists()) {
            const { usedDays, pendingDays } = await calculateExistingLeaveUsage(employeeId, employee.name);
            const remainingDays = Math.max(0, totalDays - usedDays - pendingDays);

            const balanceData: AnnualLeaveBalance = {
              employeeId,
              employeeName: employee.name,
              teamId: employee.team || '',
              hireDate: Timestamp.fromDate(hireDate),
              anniversaryDate: Timestamp.fromDate(currentAnniversaryDate),
              seniorityYears: currentSeniorityYears,
              validFrom: Timestamp.fromDate(validFrom),
              validUntil: Timestamp.fromDate(validUntil),
              totalDays,
              usedDays,
              remainingDays,
              pendingDays,
              isLargeWorkplace,
              referenceMonth: formatYearMonth(currentAnniversaryDate),
              monthlyLeaveDays: 0,
              expiredMonthlyDays: 0,
              expiredAnnualDays: 0,
              locked: false,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            };

            await setDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId), balanceData);

            const usageInfo = usedDays > 0 || pendingDays > 0
              ? `, 기존사용: ${usedDays}일, 대기: ${pendingDays}일, 잔여: ${remainingDays}일`
              : '';
            details.push(
              `✅ ${employee.name}: 연차 ${totalDays}일 생성 ` +
              `(${validFrom.toLocaleDateString('ko-KR')} ~ ${validUntil.toLocaleDateString('ko-KR')}, ` +
              `근속 ${currentSeniorityYears}년${usageInfo})`
            );
            successCount++;
          } else if (forceRecalculate) {
            const existingData = existingDoc.data();
            const usedDays = existingData.usedDays || 0;
            const pendingDays = existingData.pendingDays || 0;
            const oldTotalDays = existingData.totalDays || 0;

            const needsUpdate = existingData.isLargeWorkplace !== isLargeWorkplace || oldTotalDays !== totalDays;

            if (needsUpdate) {
              const newRemainingDays = Math.max(0, totalDays - usedDays - pendingDays);

              await updateDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId), {
                totalDays,
                remainingDays: newRemainingDays,
                isLargeWorkplace,
                seniorityYears: currentSeniorityYears,
                referenceMonth: formatYearMonth(currentAnniversaryDate),
                updatedAt: Timestamp.now(),
              });

              details.push(
                `🔄 ${employee.name}: 연차 재계산 완료 ` +
                `${oldTotalDays}일 → ${totalDays}일 ` +
                `(사용: ${usedDays}일, 남은: ${newRemainingDays}일)`
              );
              updatedCount++;
            } else {
              skippedCount++;
            }
          } else {
            skippedCount++;
          }
        }
        // 1년 미만: 월차 처리
        else {
          const monthlyDays = Math.min(seniorityMonths, 11);
          const docId = `${employeeId}_monthly`;

          const validFrom = hireDate;
          const validUntil = new Date(hireDate);
          validUntil.setFullYear(validUntil.getFullYear() + 1);
          validUntil.setDate(validUntil.getDate() - 1);

          const existingDoc = await getDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId));

          if (!existingDoc.exists()) {
            const { usedDays, pendingDays } = await calculateExistingLeaveUsage(employeeId, employee.name);
            const remainingDays = Math.max(0, monthlyDays - usedDays - pendingDays);

            const balanceData: AnnualLeaveBalance = {
              employeeId,
              employeeName: employee.name,
              teamId: employee.team || '',
              hireDate: Timestamp.fromDate(hireDate),
              anniversaryDate: Timestamp.fromDate(hireDate),
              seniorityYears: 0,
              validFrom: Timestamp.fromDate(validFrom),
              validUntil: Timestamp.fromDate(validUntil),
              totalDays: 0,
              usedDays,
              remainingDays,
              pendingDays,
              isLargeWorkplace: false,
              referenceMonth: formatYearMonth(now),
              monthlyLeaveDays: monthlyDays,
              expiredMonthlyDays: 0,
              expiredAnnualDays: 0,
              locked: false,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            };

            await setDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId), balanceData);

            const usageInfo = usedDays > 0 || pendingDays > 0
              ? `, 기존사용: ${usedDays}일, 대기: ${pendingDays}일, 잔여: ${remainingDays}일`
              : '';
            details.push(
              `✅ ${employee.name}: 월차 ${monthlyDays}일 생성 ` +
              `(${validFrom.toLocaleDateString('ko-KR')} ~ ${validUntil.toLocaleDateString('ko-KR')}, ` +
              `근속 ${seniorityMonths}개월${usageInfo})`
            );
            successCount++;
          } else if (forceRecalculate) {
            const existingData = existingDoc.data();
            const usedDays = existingData.usedDays || 0;
            // 승인대기(pending) 연차도 잔여에서 차감해야 함. 최초생성·연차 경로 및
            // syncLeaveBalanceUsedDays와 동일한 불변식(remaining = total - used - pending) 유지.
            const pendingDays = existingData.pendingDays || 0;
            const newRemainingDays = Math.max(0, monthlyDays - usedDays - pendingDays);

            await updateDoc(doc(db, COLLECTIONS.ANNUAL_LEAVE_BALANCES, docId), {
              monthlyLeaveDays: monthlyDays,
              remainingDays: newRemainingDays,
              referenceMonth: formatYearMonth(now),
              updatedAt: Timestamp.now(),
            });

            details.push(
              `🔄 ${employee.name}: 월차 재계산 완료 ` +
              `${existingData.monthlyLeaveDays || 0}일 → ${monthlyDays}일 ` +
              `(사용: ${usedDays}일, 대기: ${pendingDays}일, 남은: ${newRemainingDays}일)`
            );
            updatedCount++;
          } else {
            skippedCount++;
          }
        }
      } catch (error) {
        details.push(`❌ ${employee.name || '직원'} 처리 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
        failedCount++;
      }
    }

    return {
      success: successCount,
      failed: failedCount,
      skipped: skippedCount,
      updated: updatedCount,
      details,
    };
  } catch (error) {
    logger.error('[initializeAllLeaveBalances] 전체 실패:', error instanceof Error ? error : undefined);
    throw error;
  }
};